import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { ApiService } from '../services/apiService';
import { useReturningCustomerFlow } from '../hooks/useReturningCustomerFlow';
import EmailEntryLayout from '../components/EmailEntryLayout';
import EmailValidationForm from '../components/EmailValidation';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import { DEV_CARD_ID, DEV_PREVIEW_USER } from '../dev/devPreviewMocks';
import './EmailValidation.css';

// @mcc-dev-preview:start dev-preview-types
type EmailValidationDevState =
  | 'form'
  | 'token-loading'
  | 'token-success'
  | 'token-error'
  | 'returning-customer-dialog';

const EMAIL_VALIDATION_DEV_STATES: EmailValidationDevState[] = [
  'form',
  'token-loading',
  'token-success',
  'token-error',
  'returning-customer-dialog',
];
// @mcc-dev-preview:end dev-preview-types

const TERMS_URL = 'https://www.mycryptocard.io/terms';
const PRIVACY_URL = 'https://www.mycryptocard.io/privacy';

interface ValidationState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message: string;
  user?: any;
}

const EmailValidation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const devState = parseDevState(searchParams, EMAIL_VALIDATION_DEV_STATES);
  const [validationState, setValidationState] = useState<ValidationState>({
    status: 'idle',
    message: ''
  });
  const [sendError, setSendError] = useState('');
  const [isSending, setIsSending] = useState(false);

  const token = searchParams.get('token');
  const cardId = searchParams.get('cardId');
  const returnTo = searchParams.get('returnTo');

  const {
    showCardLinkingDialog,
    returningCustomerName,
    handleReturningCustomer,
    handleDialogOk,
    handleDialogCancel
  } = useReturningCustomerFlow();

  const validateToken = useCallback(async (validationToken: string, cId: string) => {
    try {
      setValidationState({
        status: 'loading',
        message: 'Validating your email...'
      });

      const result = await ApiService.validateEmailToken(validationToken, cId);

      if (result.success) {
        setValidationState({
          status: 'success',
          message: 'Email validated successfully!',
          user: result.data
        });

        if (result.data.jwtToken) {
          localStorage.setItem('authToken', result.data.jwtToken);
          localStorage.setItem('user', JSON.stringify(result.data));
        }
        if (result.data && result.data.jwtToken) {
          AuthService.setToken(result.data.jwtToken, {
            userEmail: result.data.userEmail,
            ...result.data
          });
        }

        handleReturningCustomer(
          result.data.isReturningCustomer || false,
          result.data.firstName || '',
          cId || '',
          returnTo
        );

        if (!result.data.isReturningCustomer) {
          setTimeout(() => {
            if (returnTo) {
              navigate(returnTo);
            } else {
              navigate('/purchase-crypto?cardId=' + cId);
            }
          }, 2000);
        }
      } else {
        setValidationState({
          status: 'error',
          message: result.message || 'Validation failed'
        });
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setValidationState({
        status: 'error',
        message: 'Network error. Please try again.'
      });
    }
  }, [navigate, returnTo, handleReturningCustomer]);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    const previewCardId = cardId || searchParams.get('cardId') || DEV_CARD_ID;

    switch (devState) {
      case 'form':
        break;
      case 'token-loading':
        setValidationState({ status: 'loading', message: 'Validating your email...' });
        break;
      case 'token-success':
        setValidationState({
          status: 'success',
          message: 'Email validated successfully!',
        });
        break;
      case 'token-error':
        setValidationState({
          status: 'error',
          message: searchParams.get('errorMessage') ?? 'Validation failed',
        });
        break;
      case 'returning-customer-dialog':
        setValidationState({
          status: 'success',
          message: 'Email validated successfully!',
        });
        handleReturningCustomer(
          true,
          DEV_PREVIEW_USER.firstName,
          previewCardId,
          returnTo
        );
        break;
      default:
        break;
    }
  }, [devState, searchParams, cardId, returnTo, handleReturningCustomer]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;
    if (token && cardId) {
      validateToken(token, cardId);
    }
  }, [token, cardId, validateToken, devState]);

  const handleEmailValidationSuccess = async (email: string) => {
    if (devState) return;
    if (!cardId) {
      setSendError('Missing card context. Please use the link from your card.');
      return;
    }
    setSendError('');
    setIsSending(true);
    try {
      await ApiService.sendVerificationEmail(email.trim(), cardId, returnTo || undefined);
      navigate('/email-sent', { state: { email: email.trim(), cardId } });
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send verification email.');
    } finally {
      setIsSending(false);
    }
  };

  const handleEmailValidationError = (error: string) => {
    setSendError(error);
  };

  const handleResendEmail = () => {
    // TODO: Implement resend validation email
  };

  const renderCardContent = () => {
    const showTokenFlow = devState ? devState !== 'form' : !!(token && cardId);

    if (showTokenFlow) {
      switch (validationState.status) {
        case 'loading':
          return (
            <div className="validation-container ev-card-content">
              <div className="loading-spinner"></div>
              <h2 className="ev-headline">Validating your email...</h2>
              <p className="ev-subtext">{validationState.message}</p>
            </div>
          );

        case 'success':
          return (
            <div className="validation-container success ev-card-content">
              <div className="success-icon">✓</div>
              <h2 className="ev-headline">Email Validated!</h2>
              <p className="ev-subtext">{validationState.message}</p>
              <p className="ev-subtext">Redirecting to your requested page...</p>
            </div>
          );

        case 'error':
          return (
            <div className="validation-container error ev-card-content">
              <div className="error-icon">✗</div>
              <h2 className="ev-headline">Validation Failed</h2>
              <p className="ev-subtext">{validationState.message}</p>
              <div className="error-actions">
                <button type="button" onClick={handleResendEmail} className="ev-btn ev-btn-primary">
                  Resend Validation Email
                </button>
                <button type="button" onClick={() => navigate('/welcome' + (cardId ? '?cardId=' + cardId : ''))} className="ev-btn ev-btn-secondary">
                  Return to Home
                </button>
              </div>
            </div>
          );

        default:
          return (
            <div className="validation-container ev-card-content">
              <h2 className="ev-headline">Email Validation</h2>
              <p className="ev-subtext">Please wait while we process your validation...</p>
            </div>
          );
      }
    }

    return (
      <>
        {sendError && <p className="ev-error-text" role="alert">{sendError}</p>}
        <EmailValidationForm
          onValidationSuccess={handleEmailValidationSuccess}
          onValidationError={handleEmailValidationError}
          isLoading={isSending}
          disabled={isSending}
          showDisclaimer={true}
          disclaimerContent={
            <>
              By clicking Send Verification Email, I agree to the{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="ev-link">Terms and Services</a>
              {' '}and{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="ev-link">Privacy Policy</a>.
              By entering my email address, I agree to receive transactional emails.
            </>
          }
        />
      </>
    );
  };

  return (
    <>
      <EmailEntryLayout className={['error', 'success', 'loading'].includes(validationState.status) ? 'error-state' : ''}>
        {renderCardContent()}
      </EmailEntryLayout>

      {showCardLinkingDialog && (
        <div className="card-linking-dialog-overlay">
          <div className="card-linking-dialog">
            <div className="card-linking-dialog-content ev-dialog-like">
              <h2 className="ev-headline">Welcome back{returningCustomerName ? ` ${returningCustomerName}` : ''}!</h2>
              <p className="ev-subtext">If you continue, this card will be linked to your existing email and the ID provided last time.</p>
              <div className="card-linking-dialog-buttons">
                <button type="button" onClick={handleDialogOk} className="ev-btn ev-btn-primary">
                  OK
                </button>
                <button type="button" onClick={handleDialogCancel} className="ev-btn ev-btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailValidation;
